import React from "react";
import { Helmet } from "react-helmet";
import { Accordion, AccordionDetails, AccordionSummary, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

interface FaqProps {
  entityLegalName: string;
  entityLegalAddressLines: string;
  entityLegalAddressCity: string;
  entityLegalAddressRegion: string;
  entityLegalAddressCountry: string;
  entityLegalAddressPostalCode: string;
  entityHeadquartersAddressLines: string;
  entityHeadquartersAddressCity: string;
  entityHeadquartersAddressRegion: string;
  entityHeadquartersAddressCountry: string;
  entityHeadquartersAddressPostalCode: string;
  legalFormTransliteratedName: string;
  entityCreationDate: string;
  countries: Record<string, string>;
}

export const Faq: React.FC<FaqProps> = ({
  entityLegalName,
  entityLegalAddressLines,
  entityLegalAddressCity,
  entityLegalAddressRegion,
  entityLegalAddressCountry,
  entityLegalAddressPostalCode,
  entityHeadquartersAddressLines,
  entityHeadquartersAddressCity,
  entityHeadquartersAddressRegion,
  entityHeadquartersAddressCountry,
  entityHeadquartersAddressPostalCode,
  legalFormTransliteratedName,
  entityCreationDate,
  countries,
}) => {
  // `countries` only covers the jurisdictions LEIpapa issues NEW LEIs for,
  // not every GLEIF jurisdiction - an existing entity can be registered
  // somewhere outside that list, so fall back to the raw code rather than
  // rendering "undefined".
  const countryName = (code: string) => countries[code] || code;

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": `What is the legal address of ${entityLegalName}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `${entityLegalName} has its legal address at ${entityLegalAddressLines}, ${entityLegalAddressCity}, ${entityLegalAddressRegion} - ${countryName(entityLegalAddressCountry)}, ${entityLegalAddressPostalCode}.`,
        },
      },
      {
        "@type": "Question",
        "name": `Where is ${entityLegalName} headquartered?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `${entityLegalName} has its headquarters (HQ) address at ${entityHeadquartersAddressLines}, ${entityHeadquartersAddressCity}, ${entityHeadquartersAddressRegion} - ${countryName(entityHeadquartersAddressCountry)}, ${entityHeadquartersAddressPostalCode}.`,
        },
      },
      {
        "@type": "Question",
        "name": `What is ${entityLegalName} legal form?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `${entityLegalName} is registered as a ${legalFormTransliteratedName}.`,
        },
      },
      {
        "@type": "Question",
        "name": `When was ${entityLegalName} registered?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `${entityLegalName} was registered on ${entityCreationDate}.`,
        },
      },
      {
        "@type": "Question",
        "name": `How to register an LEI code in ${countryName(entityLegalAddressCountry)}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `To register an LEI code for the entity from ${countryName(entityLegalAddressCountry)}, please go to the "Register an LEI code" section of the app and enter the details of your entity. After the payment, you will get your LEI code shortly.`,
        },
      },
      {
        "@type": "Question",
        "name": `How to transfer my LEI code to LEIpapa?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `To transfer your existing LEI code to LEIpapa, please go to the "Transfer an LEI code" section of the app and enter the details of your entity. Also, read the full information on LEI transfer on our website.`,
        },
      },
    ],
  };

  const items: { question: string; answer: React.ReactNode }[] = [
    {
      question: `What is the legal address of ${entityLegalName}?`,
      answer: (
        <>
          {entityLegalName} has its legal address at {entityLegalAddressLines},{" "}
          {entityLegalAddressCity}, {entityLegalAddressRegion} -{" "}
          {countryName(entityLegalAddressCountry)}, {entityLegalAddressPostalCode}.
        </>
      ),
    },
    {
      question: `Where is ${entityLegalName} headquartered?`,
      answer: (
        <>
          {entityLegalName} has its headquarters (HQ) address at{" "}
          {entityHeadquartersAddressLines}, {entityHeadquartersAddressCity},{" "}
          {entityHeadquartersAddressRegion} -{" "}
          {countryName(entityHeadquartersAddressCountry)},{" "}
          {entityHeadquartersAddressPostalCode}.
        </>
      ),
    },
    {
      question: `What is ${entityLegalName} legal form?`,
      answer: <>{entityLegalName} is registered as a {legalFormTransliteratedName}.</>,
    },
    {
      question: `When was ${entityLegalName} registered?`,
      answer: <>{entityLegalName} was registered on {entityCreationDate}.</>,
    },
    {
      question: `How to register an LEI code in ${countryName(entityLegalAddressCountry)}?`,
      answer: (
        <>
          To register an LEI code for the entity from{" "}
          {countryName(entityLegalAddressCountry)}, please go to the{" "}
          <a href="https://app.leipapa.com/create" title="Register an LEI">
            "Register an LEI code"
          </a>{" "}
          section of the app and enter the details of your entity. After the
          payment, you will get your LEI code shortly.
        </>
      ),
    },
    {
      question: `How to transfer my LEI code to LEIpapa?`,
      answer: (
        <>
          To transfer your existing LEI code to LEIpapa, please go to the{" "}
          <a href="https://app.leipapa.com/transfer" title="Transfer an LEI">
            "Transfer an LEI code"
          </a>{" "}
          section of the app and enter the details of your entity. Also,{" "}
          <a
            href="https://leipapa.com/docs/transfer-an-lei/"
            title="Transfer an LEI"
            target="_blank"
            rel="noreferrer"
          >
            read the full information
          </a>{" "}
          on LEI transfer on our website.
        </>
      ),
    },
  ];

  return (
    <div>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {items.map((item, index) => (
        <Accordion key={index} disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" component="h3" fontWeight={700}>
              {item.question}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography sx={{ lineHeight: 1.5 }}>{item.answer}</Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </div>
  );
};
